import { z } from 'zod'

const SafeKycRequirementSchema = z.object({
  id: z.string(),
  status: z.enum(['past_due', 'currently_due', 'pending_verification', 'eventually_due']),
  source: z.enum(['current', 'future']),
  deadline: z.string().nullable(),
  errorCode: z.string().nullable(),
})

const AdminBankAccountSchema = z.object({
  id: z.string(),
  bankName: z.string().nullable(),
  last4: z.string().nullable(),
  status: z.enum(['new', 'validated', 'verified', 'verification_failed', 'errored', 'unknown']),
  role: z.enum(['active', 'pending_replacement', 'previous']),
})

const AdminBankReadinessReasonSchema = z.enum([
  'not_observed',
  'no_default_account',
  'default_account_errored',
  'default_account_verification_failed',
  'external_account_required',
  'payouts_disabled',
])

const AdminBankSummarySchema = z.object({
  accounts: z.array(AdminBankAccountSchema),
  readiness: z.object({
    ready: z.boolean(),
    reason: AdminBankReadinessReasonSchema.nullable(),
  }),
})

const AdminSyncFailureSchema = z.object({ at: z.string(), code: z.string() })

const AdminKycStatusSchema = z.enum([
  'verified',
  'pending_verification',
  'action_required',
  'rejected',
])

const AdminKycNextActionSchema = z.enum([
  'none',
  'provide_information',
  'await_stripe_review',
  'contact_support',
])

const AdminEligibilityReasonSchema = z.enum([
  'connect_not_configured',
  'not_linked',
  'user_not_found',
  'status_unavailable',
  'kyc_pending_verification',
  'kyc_action_required',
  'kyc_rejected',
  'card_payments_not_active',
  'transfers_not_active',
  'bank_no_default_account',
  'bank_default_account_errored',
  'bank_default_account_verification_failed',
  'bank_external_account_required',
  'bank_payouts_disabled',
  'reserve_not_configured',
  'reserve_initial_pending',
  'reserve_initial_failed',
  'reserve_initial_dead',
])

const AdminEligibilityCheckSchema = z.object({
  status: z.enum(['passed', 'failed', 'not_evaluated']),
  reasons: z.array(AdminEligibilityReasonSchema),
})

const AdminEligibilitySchema = z.object({
  approved: z.boolean(),
  reasons: z.array(AdminEligibilityReasonSchema),
  checks: z.object({
    link: AdminEligibilityCheckSchema,
    kyc: AdminEligibilityCheckSchema,
    capabilities: AdminEligibilityCheckSchema,
    bank: AdminEligibilityCheckSchema,
    reserve: AdminEligibilityCheckSchema,
  }),
  kycStatus: AdminKycStatusSchema.nullable(),
  observedAt: z.string().nullable(),
  syncFailure: AdminSyncFailureSchema.nullable(),
  disabledReason: z.string().nullable().optional(),
  capabilities: z.record(z.string(), z.enum(['active', 'inactive', 'pending'])).optional(),
})

const AdminOnboardingNextStepSchema = z.object({
  owner: z.enum(['seller', 'stripe', 'support', 'none']),
  code: z.enum([
    'save_profile',
    'fix_field',
    'identity_document',
    'add_bank_account',
    'fix_bank_account',
    'wait_review',
    'rejected',
    'activation_failed',
    'unsupported_requirement',
    'account_disabled',
    'sync_failing',
    'none',
  ]),
  requirementId: z.string().optional(),
  since: z.string().optional(),
})

const AdminOnboardingSchema = z.object({
  badge: z.enum(['in_progress', 'in_review', 'correction_needed', 'approved']),
  steps: z.object({
    profile: z.enum(['pending', 'in_review', 'fix', 'done']),
    identity: z.enum(['unavailable', 'preparing', 'pending', 'in_review', 'fix', 'done']),
    bank: z.enum(['unavailable', 'pending', 'fix', 'done']),
  }),
  operations: z.object({
    sales: z.enum(['waiting', 'in_review', 'released', 'blocked']),
    payouts: z.enum(['waiting', 'in_review', 'released', 'blocked']),
  }),
  blockedBy: z
    .object({
      sales: z.enum(['vendepay', 'stripe']).nullable(),
      payouts: z.enum(['vendepay', 'stripe']).nullable(),
    })
    .default({ sales: null, payouts: null }),
  nextStep: AdminOnboardingNextStepSchema,
})

const AdminReserveSchema = z.object({
  status: z.enum(['not_configured', 'pending', 'applied', 'failed', 'dead']),
  kind: z.enum(['initial', 'update']).nullable(),
  desired: z
    .object({
      percent: z.string(),
      daysAfterCharge: z.number(),
      currency: z.string(),
      readAt: z.string(),
    })
    .nullable(),
  applied: z
    .object({
      percent: z.number(),
      daysAfterCharge: z.number(),
      currency: z.string(),
      appliedAt: z.string(),
    })
    .nullable(),
  attempts: z.number(),
  lastErrorCode: z.string().nullable(),
  updatedAt: z.string().nullable(),
  readiness: z.object({
    ready: z.boolean(),
    reason: z
      .enum(['not_configured', 'initial_pending', 'initial_failed', 'initial_dead'])
      .nullable(),
  }),
})

export const AdminKycVerificationSchema = z.object({
  linked: z.boolean(),
  onboarding: AdminOnboardingSchema,
  bank: AdminBankSummarySchema.nullable(),
  eligibility: AdminEligibilitySchema,
  reserve: AdminReserveSchema.nullable(),
  submission: z.object({
    state: z.enum(['not_linked', 'not_submitted', 'pending', 'failed', 'accepted']),
    unsentChanges: z.boolean(),
    last: z
      .object({
        id: z.string(),
        status: z.enum(['pending', 'accepted', 'failed']),
        actorType: z.enum(['participant', 'staff', 'system']),
        createdAt: z.string(),
        completedAt: z.string().nullable(),
        failureCode: z.string().nullable(),
        failureParam: z.string().nullable(),
      })
      .nullable(),
    lastAcceptedAt: z.string().nullable(),
  }),
  verification: z
    .object({
      kycStatus: AdminKycStatusSchema,
      nextAction: AdminKycNextActionSchema,
      currentDeadline: z.string().nullable(),
      observedAt: z.string().nullable(),
      syncFailure: AdminSyncFailureSchema.nullable(),
      requirements: z.object({
        form: z.array(SafeKycRequirementSchema),
        embedded: z.array(SafeKycRequirementSchema),
        support: z.array(SafeKycRequirementSchema),
        bank_account: z.array(SafeKycRequirementSchema),
      }),
    })
    .nullable(),
})

export type AdminKycVerification = z.infer<typeof AdminKycVerificationSchema>
export type AdminOnboarding = z.infer<typeof AdminOnboardingSchema>
export type AdminOnboardingNextStep = z.infer<typeof AdminOnboardingNextStepSchema>
export type AdminOnboardingBlockOwner = z.infer<typeof AdminOnboardingSchema>['blockedBy']['sales']
export type AdminBankSummary = z.infer<typeof AdminBankSummarySchema>
export type AdminBankAccount = z.infer<typeof AdminBankAccountSchema>

export const AdminKycActionRequiredSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  businessType: z.enum(['PF', 'PJ']).nullable(),
  onboarding: z.object({ nextStep: AdminOnboardingNextStepSchema }),
  date: z.string().nullable(),
})

export const AdminKycActionRequiredPageSchema = z.object({
  items: z.array(AdminKycActionRequiredSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export const AdminKycSyncResultSchema = z.object({
  outcome: z.enum(['synced', 'superseded', 'failed', 'not_linked']),
  verification: AdminKycVerificationSchema,
})

export type AdminKycActionRequired = z.infer<typeof AdminKycActionRequiredSchema>
export type EligibilityReasonCode = z.infer<typeof AdminEligibilityReasonSchema>
export type AdminKycStatus = z.infer<typeof AdminKycStatusSchema>
export type AdminKycNextAction = z.infer<typeof AdminKycNextActionSchema>
export type AdminEligibility = z.infer<typeof AdminEligibilitySchema>
export type AdminReserve = z.infer<typeof AdminReserveSchema>
export type AdminKycSyncResult = z.infer<typeof AdminKycSyncResultSchema>
